import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Paper, Button, TextField,
  Chip, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, CircularProgress, IconButton, Switch, FormControlLabel, Snackbar,
} from '@mui/material';
import {
  Settings, Edit, Save, Cancel, EventAvailable, FreeBreakfast,
} from '@mui/icons-material';
import {
  useGetLeaveConfigQuery,
  useUpdateLeaveConfigMutation,
} from '../../../slices/annualLeaveApiSlice';
import { useLayout } from '../../../context/LayoutContext';

// Full list of staff categories the app knows about — mirrors the list on
// both calculator screens' Config tabs.
const fmtHrs = (h) => {
  if (h === null || h === undefined || h === '' || Number.isNaN(Number(h))) return '—';
  const totalMinutes = Math.round(Number(h) * 60);
  const sign = totalMinutes < 0 ? '-' : '';
  const abs = Math.abs(totalMinutes);
  const hrs = Math.floor(abs / 60);
  const mins = abs % 60;
  return `${sign}${hrs}:${String(mins).padStart(2, '0')}`;
};

const DEFAULT_TIERS = [
  { uptoHours: 4, breakHours: 0 },
  { uptoHours: 6, breakHours: 0.5 },
  { uptoHours: 24, breakHours: 1 },   // 6+ hours
];

// Accepts either the new { default, byCategory } shape, or the old flat
// { shortThresholdHours, mediumBreakHours, longThresholdHours, longBreakHours }
// shape, so existing saved configs keep working after this change.
const normalizeBreakRules = (raw) => {
  if (!raw) return { default: { tiers: DEFAULT_TIERS.map((t) => ({ ...t })) }, byCategory: {} };
  if (raw.default || raw.byCategory) {
    return { default: raw.default || { tiers: DEFAULT_TIERS.map((t) => ({ ...t })) }, byCategory: raw.byCategory || {} };
  }
  const { shortThresholdHours = 4, mediumBreakHours = 0.5, longThresholdHours = 8, longBreakHours = 1 } = raw;
  return {
    default: {
      tiers: [
        { uptoHours: shortThresholdHours, breakHours: 0 },
        { uptoHours: longThresholdHours, breakHours: mediumBreakHours },
        { uptoHours: 24, breakHours: longBreakHours },
      ],
    },
    byCategory: {},
  };
};

// Given a tier list and a gross duration, find the applicable break deduction.
// Mirrors the backend's getBreakDeductionHours logic exactly, so the preview
// in the UI always matches what the server will actually calculate.
const deductionForTiers = (tiers, grossHours) => {
  if (!Array.isArray(tiers) || tiers.length === 0) return 0;
  const sorted = [...tiers].sort((a, b) => a.uptoHours - b.uptoHours);
  for (const tier of sorted) {
    if (grossHours <= tier.uptoHours) return tier.breakHours ?? 0;
  }
  return sorted[sorted.length - 1].breakHours ?? 0;
};

function BreakTierEditor({ title, tiers, editable, onChangeTier, onAddTier, onRemoveTier, compact }) {
  const sample = [3, 4, 5, 6, 7, 8, 10, 12];
  return (
    <Box>
      {title && <Typography variant="subtitle2" gutterBottom>{title}</Typography>}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {tiers.map((tier, idx) => (
          <Grid item xs={12} sm={6} md={compact ? 4 : 3} key={idx}>
            <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small" type="number" label="Up to (hrs)"
                    value={tier.uptoHours} disabled={!editable}
                    onChange={(e) => onChangeTier(idx, 'uptoHours', e.target.value)}
                    inputProps={{ min: 0, max: 24, step: 0.25 }}
                  />
                  <Typography variant="caption" color="text.secondary">= {fmtHrs(tier.uptoHours)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small" type="number" label="Break (hrs)"
                    value={tier.breakHours} disabled={!editable}
                    onChange={(e) => onChangeTier(idx, 'breakHours', e.target.value)}
                    inputProps={{ min: 0, max: 4, step: 0.25 }}
                  />
                  <Typography variant="caption" color="text.secondary">= {fmtHrs(tier.breakHours)}</Typography>
                </Grid>
              </Grid>
              {editable && tiers.length > 1 && (
                <Button size="small" color="error" sx={{ mt: 1 }} onClick={() => onRemoveTier(idx)}>
                  Remove Tier
                </Button>
              )}
              {idx === tiers.length - 1 && (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                  Also applies to anything above this
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
      {editable && <Button size="small" onClick={onAddTier} sx={{ mb: 2 }}>+ Add Tier</Button>}
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Preview</Typography>
      <Grid container spacing={1}>
        {sample.map((g) => {
          const ded = deductionForTiers(tiers, g);
          return (
            <Grid item xs={6} sm={3} md={1.5} key={g}>
              <Box sx={{ p: 1, border: '1px solid #eee', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">{fmtHrs(g)} gross</Typography>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                  {'−'}{fmtHrs(ded)} {'→'} {fmtHrs(Math.max(0, g - ded))}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

export default function AnnualLeaveConfigScreen() {
  const { setFluid } = useLayout();
  useEffect(() => { setFluid(true); return () => setFluid(false); }, [setFluid]);

  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [editingConfig, setEditingConfig] = useState(false);
  const [localConfig,   setLocalConfig]   = useState(null);
  const [newStaffCategory, setNewStaffCategory] = useState('');

  const { data: serverConfig, isLoading: configLoading } = useGetLeaveConfigQuery();
  const [updateConfig, { isLoading: saving }] = useUpdateLeaveConfigMutation();

  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  useEffect(() => {
    if (serverConfig && !localConfig) {
      const cloned = JSON.parse(JSON.stringify(serverConfig));
      cloned.breakDeductionRules = normalizeBreakRules(cloned.breakDeductionRules);
      setLocalConfig(cloned);
    }
  }, [serverConfig]);

  const handleCancel = () => {
    const cloned = JSON.parse(JSON.stringify(serverConfig));
    cloned.breakDeductionRules = normalizeBreakRules(cloned.breakDeductionRules);
    setLocalConfig(cloned);
    setEditingConfig(false);
  };

  const handleSaveConfig = async () => {
    try {
      await updateConfig({
        accrualRate:             localConfig.accrualRate,
        method1Threshold:        localConfig.method1Threshold,
        method2MonthlyThreshold: localConfig.method2MonthlyThreshold,
        categoryHours:           localConfig.categoryHours,
        staffCategories:         localConfig.staffCategories,
        leaveTypeRules:          localConfig.leaveTypeRules,
        publicHolidayConfig:     localConfig.publicHolidayConfig,
        publicHolidays:          localConfig.publicHolidays,
        breakDeductionRules:     localConfig.breakDeductionRules,
        calculationPeriodStartDate: localConfig.calculationPeriodStartDate || null,
      }).unwrap();
      showSnack('Configuration saved', 'success');
      setEditingConfig(false);
    } catch (err) {
      showSnack(err?.data?.message || 'Failed to save config', 'error');
    }
  };

  // ── Config helpers ──────────────────────────────────────────────────────
  const toggleStaffCategory = (category) => {
    setLocalConfig((prev) => {
      const current = prev.staffCategories || [];
      const next = current.includes(category)
        ? current.filter((c) => c !== category)
        : [...current, category];
      return { ...prev, staffCategories: next };
    });
  };
  const addCustomStaffCategory = (category) => {
    const trimmed = (category || '').trim();
    if (!trimmed) return;
    setLocalConfig((prev) => {
      const current = prev.staffCategories || [];
      return current.includes(trimmed) ? prev : { ...prev, staffCategories: [...current, trimmed] };
    });
  };
  const updateCatHours = (category, weeklyHours) => {
    setLocalConfig((prev) => ({
      ...prev,
      categoryHours: prev.categoryHours.map((c) =>
        c.category === category ? { ...c, weeklyHours: Number(weeklyHours) } : c
      ),
    }));
  };
  const updateLeaveRule = (reason, field, value) => {
    setLocalConfig((prev) => ({
      ...prev,
      leaveTypeRules: prev.leaveTypeRules.map((r) =>
        r.reason === reason ? { ...r, [field]: value } : r
      ),
    }));
  };
  const updatePHConfig = (field, value) => {
    setLocalConfig((prev) => ({
      ...prev,
      publicHolidayConfig: { ...prev.publicHolidayConfig, [field]: value },
    }));
  };

  const updateDefaultTierField = (idx, field, value) => {
    setLocalConfig((prev) => {
      const tiers = [...(prev.breakDeductionRules?.default?.tiers || DEFAULT_TIERS)];
      tiers[idx] = { ...tiers[idx], [field]: Number(value) };
      return { ...prev, breakDeductionRules: { ...prev.breakDeductionRules, default: { tiers } } };
    });
  };
  const addDefaultTier = () => {
    setLocalConfig((prev) => {
      const tiers = [...(prev.breakDeductionRules?.default?.tiers || DEFAULT_TIERS)];
      const lastUpto = tiers.length ? tiers[tiers.length - 1].uptoHours : 0;
      tiers.push({ uptoHours: lastUpto + 2, breakHours: 1 });
      return { ...prev, breakDeductionRules: { ...prev.breakDeductionRules, default: { tiers } } };
    });
  };
  const removeDefaultTier = (idx) => {
    setLocalConfig((prev) => ({
      ...prev,
      breakDeductionRules: {
        ...prev.breakDeductionRules,
        default: { tiers: (prev.breakDeductionRules?.default?.tiers || DEFAULT_TIERS).filter((_, i) => i !== idx) },
      },
    }));
  };

  const updateCategoryTierField = (category, idx, field, value) => {
    setLocalConfig((prev) => {
      const byCategory = { ...(prev.breakDeductionRules?.byCategory || {}) };
      const tiers = [...(byCategory[category]?.tiers || [])];
      tiers[idx] = { ...tiers[idx], [field]: Number(value) };
      byCategory[category] = { tiers };
      return { ...prev, breakDeductionRules: { ...prev.breakDeductionRules, byCategory } };
    });
  };
  const addCategoryTier = (category) => {
    setLocalConfig((prev) => {
      const byCategory = { ...(prev.breakDeductionRules?.byCategory || {}) };
      const tiers = [...(byCategory[category]?.tiers || [])];
      const lastUpto = tiers.length ? tiers[tiers.length - 1].uptoHours : 0;
      tiers.push({ uptoHours: lastUpto + 2, breakHours: 1 });
      byCategory[category] = { tiers };
      return { ...prev, breakDeductionRules: { ...prev.breakDeductionRules, byCategory } };
    });
  };
  const removeCategoryTier = (category, idx) => {
    setLocalConfig((prev) => {
      const byCategory = { ...(prev.breakDeductionRules?.byCategory || {}) };
      byCategory[category] = { tiers: (byCategory[category]?.tiers || []).filter((_, i) => i !== idx) };
      return { ...prev, breakDeductionRules: { ...prev.breakDeductionRules, byCategory } };
    });
  };
  const enableCategoryOverride = (category) => {
    setLocalConfig((prev) => {
      const byCategory = { ...(prev.breakDeductionRules?.byCategory || {}) };
      byCategory[category] = { tiers: (prev.breakDeductionRules?.default?.tiers || DEFAULT_TIERS).map((t) => ({ ...t })) };
      return { ...prev, breakDeductionRules: { ...prev.breakDeductionRules, byCategory } };
    });
  };
  const removeCategoryOverride = (category) => {
    setLocalConfig((prev) => {
      const byCategory = { ...(prev.breakDeductionRules?.byCategory || {}) };
      delete byCategory[category];
      return { ...prev, breakDeductionRules: { ...prev.breakDeductionRules, byCategory } };
    });
  };

  const updateHolidayField = (idx, field, value) => {
    setLocalConfig((prev) => ({
      ...prev,
      publicHolidays: prev.publicHolidays.map((h, i) =>
        i === idx ? { ...h, [field]: value } : h
      ),
    }));
  };
  const removeHoliday = (idx) => {
    setLocalConfig((prev) => ({
      ...prev,
      publicHolidays: prev.publicHolidays.filter((_, i) => i !== idx),
    }));
  };
  const addHoliday = () => {
    setLocalConfig((prev) => ({
      ...prev,
      publicHolidays: [...(prev.publicHolidays || []), { date: '', name: '' }],
    }));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Settings sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h4">Annual Leave Configuration</Typography>
          </Box>
          <Typography variant="subtitle2" color="text.secondary">
            Shared settings — these apply to BOTH the Annual Leave Calculator (Bank staff) and the
            Non Bank Annual Leave calculator.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {editingConfig ? (
            <>
              <Button variant="contained" startIcon={<Save />} onClick={handleSaveConfig} disabled={saving}>
                {saving ? 'Saving…' : 'Save Configuration'}
              </Button>
              <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancel}>
                Cancel
              </Button>
            </>
          ) : (
            <Button variant="outlined" startIcon={<Edit />} onClick={() => setEditingConfig(true)}>
              Edit Configuration
            </Button>
          )}
        </Box>
      </Box>

      {configLoading && <CircularProgress size={24} sx={{ mb: 2 }} />}

      {localConfig && (
        <Grid container spacing={3}>
          {/* Statutory thresholds */}
          <Grid item xs={12} md={5}>
            <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
              <Typography variant="h6" gutterBottom>Statutory Thresholds</Typography>
              <Divider sx={{ mb: 2 }} />
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.78rem' }}>
                These match Organisation of Working Time Act 1997 defaults.
                Only change if legislation is updated. Used by the main Annual Leave Calculator
                (Bank-HCA / Bank-RGN) only — the Non Bank calculator doesn't run these methods.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" type="number"
                    label="Method 3 — Accrual Rate (e.g. 0.08 = 8%)"
                    value={localConfig.accrualRate}
                    onChange={(e) => setLocalConfig((p) => ({ ...p, accrualRate: Number(e.target.value) }))}
                    disabled={!editingConfig}
                    inputProps={{ step: 0.001, min: 0, max: 0.2 }}
                    helperText="Statutory: 0.08 (8%). Cap is always 4 working weeks."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" type="number"
                    label="Method 1 — Full-Time Threshold (hours/year)"
                    value={localConfig.method1Threshold}
                    onChange={(e) => setLocalConfig((p) => ({ ...p, method1Threshold: Number(e.target.value) }))}
                    disabled={!editingConfig}
                    inputProps={{ step: 1, min: 0 }}
                    helperText="Statutory: 1,365 hours in the leave year → 4 working weeks"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" type="number"
                    label="Method 2 — Monthly Threshold (hours/month)"
                    value={localConfig.method2MonthlyThreshold}
                    onChange={(e) => setLocalConfig((p) => ({ ...p, method2MonthlyThreshold: Number(e.target.value) }))}
                    disabled={!editingConfig}
                    inputProps={{ step: 1, min: 0 }}
                    helperText="Statutory: 117 hours per month → 1/3 working week"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" type="date"
                    label="Calculation Period Start Date (default)"
                    InputLabelProps={{ shrink: true }}
                    value={
                      localConfig.calculationPeriodStartDate
                        ? String(localConfig.calculationPeriodStartDate).slice(0, 10)
                        : ''
                    }
                    onChange={(e) => setLocalConfig((p) => ({ ...p, calculationPeriodStartDate: e.target.value || null }))}
                    disabled={!editingConfig}
                    helperText="Pre-fills the Start Date on this calculator's Calculation Period panel, and is what a staff member's Profile 'Recalculate' button runs from. Leave blank to default to 1 January of the current year. Update this whenever the Bring Forward cut-over date changes (e.g. an End of Year rollover mid-year)."
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Staff categories included in the main (Bank) calculator */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
              <Typography variant="h6" gutterBottom>Staff Categories Included — Annual Leave Calculator (Bank)</Typography>
              <Divider sx={{ mb: 2 }} />
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.78rem' }}>
                Only staff whose category (job title) is checked below are included when
                "All Staff (Batch)" is run on the main Annual Leave Calculator. Everyone else should
                instead be included on the Non Bank Annual Leave calculator's own staff-category list.
              </Alert>
              <Grid container spacing={0}>
                {localConfig.categoryHours.map((c) => (
                  <Grid item xs={6} sm={4} md={3} key={c.category}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={(localConfig.staffCategories || []).includes(c.category)}
                          onChange={() => toggleStaffCategory(c.category)}
                          disabled={!editingConfig}
                        />
                      }
                      label={c.category}
                    />
                  </Grid>
                ))}
                {(localConfig.staffCategories || [])
                  .filter((cat) => !localConfig.categoryHours.some((c) => c.category === cat))
                  .map((cat) => (
                    <Grid item xs={6} sm={4} md={3} key={cat}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked
                            onChange={() => toggleStaffCategory(cat)}
                            disabled={!editingConfig}
                          />
                        }
                        label={`${cat} (custom)`}
                      />
                    </Grid>
                  ))}
              </Grid>
              {editingConfig && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <TextField
                    size="small" label="Add custom category"
                    value={newStaffCategory}
                    onChange={(e) => setNewStaffCategory(e.target.value)}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => { addCustomStaffCategory(newStaffCategory); setNewStaffCategory(''); }}
                  >
                    Add
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Category weekly hours */}
          <Grid item xs={12} md={7}>
            <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
              <Typography variant="h6" gutterBottom>Weekly Hours by Category</Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Used to calculate the 4-week cap, convert weekly caps (e.g. 13 weeks
                Carer's Leave) into hours, and to compute Public Holiday hours-per-day.
              </Typography>
              <Grid container spacing={2}>
                {localConfig.categoryHours.map((cat) => (
                  <Grid item xs={6} sm={4} key={cat.category}>
                    <TextField
                      fullWidth label={cat.category} type="number" size="small"
                      value={cat.weeklyHours}
                      onChange={(e) => updateCatHours(cat.category, e.target.value)}
                      disabled={!editingConfig}
                      inputProps={{ step: 0.5, min: 1, max: 80 }}
                      InputProps={{
                        endAdornment: <Typography variant="caption" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>hrs/wk</Typography>,
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Break deduction rules */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FreeBreakfast color="primary" /> Break Deduction Rules
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                Applied to the gross clocked length of a shift, to the gross duration of any leave type
                marked "Qualifies / Accrues" below (main calculator), and to Annual Leave days taken by
                Non Bank staff before they're deducted from Bring Forward. Each job category can have
                its own tiers; categories with no custom rule use the Default below. Tiers can be added
                or removed freely — this is a business policy, not a legislative requirement.
              </Alert>

              <BreakTierEditor
                title="Default (all other staff)"
                tiers={localConfig.breakDeductionRules?.default?.tiers || DEFAULT_TIERS}
                editable={editingConfig}
                onChangeTier={updateDefaultTierField}
                onAddTier={addDefaultTier}
                onRemoveTier={removeDefaultTier}
              />

              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" gutterBottom>Category-Specific Rules</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                One row per job category. A category with no custom rule inherits the Default above.
              </Typography>

              {localConfig.categoryHours.map((cat) => {
                const override = localConfig.breakDeductionRules?.byCategory?.[cat.category];
                return (
                  <Box key={cat.category} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{cat.category}</Typography>
                      {override
                        ? <Chip label="Custom rule" size="small" color="warning" variant="outlined" />
                        : <Chip label="Uses Default" size="small" variant="outlined" />}
                      {editingConfig && (override
                        ? <Button size="small" color="error" onClick={() => removeCategoryOverride(cat.category)}>Remove Custom Rule</Button>
                        : <Button size="small" onClick={() => enableCategoryOverride(cat.category)}>Customize for {cat.category}</Button>
                      )}
                    </Box>
                    {override && (
                      <BreakTierEditor
                        tiers={override.tiers}
                        editable={editingConfig}
                        onChangeTier={(idx, field, value) => updateCategoryTierField(cat.category, idx, field, value)}
                        onAddTier={() => addCategoryTier(cat.category)}
                        onRemoveTier={(idx) => removeCategoryTier(cat.category, idx)}
                        compact
                      />
                    )}
                  </Box>
                );
              })}
            </Paper>
          </Grid>

          {/* Leave type rules */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
              <Typography variant="h6" gutterBottom>Leave Type Accrual Rules</Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Used by the main Annual Leave Calculator only. <strong>Qualifying hours</strong> are
                added to the hours base for Method 3 (net of the break deduction above).
                <strong> Weeks cap</strong> is converted to hours using the staff member's weekly
                hours, and applied after the break deduction. Set weeks cap to
                <strong> 0 or blank</strong> for unlimited qualifying hours.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Leave Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Qualifies / Accrues?</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Weeks Cap</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {localConfig.leaveTypeRules.map((rule) => (
                      <TableRow key={rule.reason} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{rule.reason}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          {editingConfig ? (
                            <Switch
                              checked={rule.accrue}
                              size="small"
                              color="success"
                              onChange={(e) => updateLeaveRule(rule.reason, 'accrue', e.target.checked)}
                            />
                          ) : (
                            <Chip
                              label={rule.accrue ? 'Yes' : 'No'}
                              size="small"
                              color={rule.accrue ? 'success' : 'default'}
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {rule.accrue ? (
                            editingConfig ? (
                              <TextField
                                type="number" size="small"
                                value={rule.weeksCap === null ? '' : rule.weeksCap}
                                onChange={(e) => updateLeaveRule(
                                  rule.reason, 'weeksCap',
                                  e.target.value === '' ? null : Number(e.target.value)
                                )}
                                inputProps={{ min: 0, max: 104 }}
                                sx={{ width: 80 }}
                                placeholder="∞"
                              />
                            ) : (
                              <Typography variant="body2">
                                {rule.weeksCap === null || rule.weeksCap === 0
                                  ? '∞ Unlimited'
                                  : `${rule.weeksCap} wks`}
                              </Typography>
                            )
                          ) : (
                            <Typography variant="caption" color="text.disabled">N/A</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {editingConfig ? (
                            <TextField
                              size="small" fullWidth value={rule.notes || ''}
                              onChange={(e) => updateLeaveRule(rule.reason, 'notes', e.target.value)}
                              placeholder="Notes…"
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">{rule.notes}</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Public Holiday Configuration */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventAvailable color="secondary" />
                Public Holiday Entitlement (Extra Day of Annual Leave)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                Organisation of Working Time Act 1997, s.21. Used by the main Annual Leave Calculator
                only — Non Bank staff already have Public Holiday time built into their Bring Forward.
                Full-time and fixed-hours staff receive (Weekly Hours ÷ Working Days/Week) per
                qualifying holiday. Part-time staff who meet the eligibility test instead receive their
                average daily hours actually worked over the configured reference period — only days
                actually worked count toward the average.
              </Alert>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={localConfig.publicHolidayConfig?.enabled !== false}
                        disabled={!editingConfig}
                        onChange={(e) => updatePHConfig('enabled', e.target.checked)}
                      />
                    }
                    label="Enabled"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth size="small" type="number" label="Working Days / Week"
                    value={localConfig.publicHolidayConfig?.workingDaysPerWeek ?? 5}
                    disabled={!editingConfig}
                    onChange={(e) => updatePHConfig('workingDaysPerWeek', Number(e.target.value))}
                    inputProps={{ min: 1, max: 7, step: 1 }}
                    helperText="Converts weekly hours → hours per holiday"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth size="small" type="number" label="Part-Time Lookback (weeks)"
                    value={localConfig.publicHolidayConfig?.partTimeLookbackWeeks ?? 5}
                    disabled={!editingConfig}
                    onChange={(e) => updatePHConfig('partTimeLookbackWeeks', Number(e.target.value))}
                    inputProps={{ min: 1, max: 26, step: 1 }}
                    helperText="Statutory: 5 weeks"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth size="small" type="number" label="Part-Time Threshold (hours)"
                    value={localConfig.publicHolidayConfig?.partTimeThresholdHours ?? 40}
                    disabled={!editingConfig}
                    onChange={(e) => updatePHConfig('partTimeThresholdHours', Number(e.target.value))}
                    inputProps={{ min: 1, max: 200, step: 1 }}
                    helperText="Statutory: 40 hours"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth size="small" type="number" label="Average Hours Lookback (weeks)"
                    value={localConfig.publicHolidayConfig?.averageHoursLookbackWeeks ?? 13}
                    disabled={!editingConfig}
                    onChange={(e) => updatePHConfig('averageHoursLookbackWeeks', Number(e.target.value))}
                    inputProps={{ min: 1, max: 52, step: 1 }}
                    helperText="Reference period for part-time average daily hours (benefit amount, separate from eligibility)"
                  />
                </Grid>
              </Grid>

              <Typography variant="subtitle2" gutterBottom>Public Holiday Dates</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Irish public holiday dates change year to year (e.g. first/last Monday
                of a month) — review and update this list annually.
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      {editingConfig && <TableCell align="center" sx={{ fontWeight: 700 }}>Remove</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(localConfig.publicHolidays || []).map((ph, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          {editingConfig ? (
                            <TextField
                              type="date" size="small" value={ph.date}
                              onChange={(e) => updateHolidayField(idx, 'date', e.target.value)}
                            />
                          ) : (
                            ph.date
                              ? new Date(ph.date).toLocaleDateString('en-IE', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })
                              : '—'
                          )}
                        </TableCell>
                        <TableCell>
                          {editingConfig ? (
                            <TextField
                              size="small" fullWidth value={ph.name}
                              onChange={(e) => updateHolidayField(idx, 'name', e.target.value)}
                            />
                          ) : (
                            <Typography variant="body2">{ph.name}</Typography>
                          )}
                        </TableCell>
                        {editingConfig && (
                          <TableCell align="center">
                            <Button size="small" color="error" onClick={() => removeHoliday(idx)}>
                              Remove
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {(!localConfig.publicHolidays || localConfig.publicHolidays.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={editingConfig ? 3 : 2}>
                          <Typography variant="caption" color="text.disabled">No holidays configured.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {editingConfig && (
                <Button size="small" sx={{ mt: 2 }} onClick={addHoliday}>
                  + Add Holiday
                </Button>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
